import { Button, Flex, Grid, Stack, Text, Textarea } from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { FiMessageSquare, FiSend } from "react-icons/fi";
import { InputField } from "components/input-field";
import { FilterCard, FilterGrid } from "../components/FilterCard";
import { SidePanelAccordion } from "../components/SidePanelAccordion";
import { State } from "components/pipeline-components/classes/state-machine/state";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { Popover } from "components/popover";
import {
  csvFields,
  firstCsvRow
} from "components/campanha-sms-components/modal-campanha-sms/csv-cols";
import { DiagnosticPanel } from "./DiagnosticPanel";

export function SmsWhatsEditor({
  selectedOperation,
  isDisabled,
  smsState,
  updatePipelineData,
  selectedStateId,
  pipelineData,
  diagnosticUpdatePipelineData
}: {
  selectedOperation: "SMS";
  isDisabled: boolean;
  smsState?: State;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  selectedStateId?: number;
  pipelineData?: StatesMachine;
  diagnosticUpdatePipelineData?: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  const stateData = smsState;
  const csvCols = csvFields.map((key) => ({ name: key, value: key }));

  const getMensagemLength = (mensagem: string = stateData?.mensagem || "") => {
    const textLength = mensagem.length;
    const uppercaseLength = textLength - mensagem.replace(/[A-Z]/g, "").length;
    const mensagemLength = textLength + uppercaseLength;
    return { mensagemLength, uppercaseLength };
  };

  const getMappedFieldsFromMessage = (mensagem: string) => {
    return csvFields
      .filter((field) => mensagem.indexOf(`$${field}`) !== -1)
      .map((field) => `$${field}`);
  };

  const sanitizeMensagem = (mensagem: string) => {
    const { uppercaseLength } = getMensagemLength(mensagem);
    const limit = Math.max(0, 160 - uppercaseLength);
    return mensagem.slice(0, limit);
  };

  const mensagemLength = getMensagemLength().mensagemLength;

  const getPreVisualizacao = () => {
    let preVisualizacao = stateData?.mensagem || "";
    csvCols.forEach(({ value }) => {
      preVisualizacao = preVisualizacao.replaceAll(
        `$${value}`,
        firstCsvRow[value] || value
      );
    });

    return preVisualizacao;
  };

  return (
    <Stack spacing="12px" mt="10px">
      <SidePanelAccordion
        icon={FiSend}
        iconBgColor="#EDE9FE"
        iconColor="#7C3AED"
        title="Campanha"
        subtitle="Nome e configurações básicas"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard
            label="Nome da Campanha"
            isDisabled={isDisabled}
            fullWidth
          >
            <InputField
              isDisabled={isDisabled}
              variant="filterInput"
              value={stateData?.nome}
              onChange={(event) =>
                updatePipelineData((draft) => {
                  draft.updateOperationState(selectedOperation, (state) => {
                    state.nome = event.target.value;
                  });
                  return;
                })
              }
            />
          </FilterCard>

          <FilterCard
            label="Qtd. máxima de telefones por lead"
            isDisabled={isDisabled}
            fullWidth
          >
            <InputField
              isDisabled={isDisabled}
              variant="filterInput"
              value={stateData?.quantidadeMaximaTelLead ?? ""}
              inputProps={{ type: "number" }}
              onChange={(event) =>
                updatePipelineData((draft) => {
                  draft.updateOperationState(selectedOperation, (state) => {
                    state.quantidadeMaximaTelLead = Number(
                      event.target.value || 0
                    );
                  });
                  return;
                })
              }
            />
          </FilterCard>
        </FilterGrid>
      </SidePanelAccordion>

      <SidePanelAccordion
        icon={FiMessageSquare}
        iconBgColor="#DBEAFE"
        iconColor="#2563EB"
        title="Mensagem"
        subtitle="Conteúdo e pré-visualização"
        activeCount={0}
      >
        <FilterGrid>
          <FilterCard label="Mensagem" isDisabled={isDisabled} fullWidth>
            <Flex mb="8px" justify="flex-end">
              <Popover
                title="Adicione campos do CSV"
                button={
                  <Button
                    aria-label=""
                    size="xs"
                    variant="outline"
                    leftIcon={<HamburgerIcon />}
                    isDisabled={isDisabled}
                  >
                    Colunas CSV
                  </Button>
                }
                popupStyles={{ width: "auto" }}
                position="left-bottom"
                isDisabled={isDisabled}
              >
                <Grid
                  w="100%"
                  gap="8px"
                  templateColumns="1fr"
                  overflow="auto"
                  maxH="200px"
                >
                  {csvCols.map(({ name, value }) => {
                    return (
                      <Button
                        key={value}
                        onClick={() => {
                          const nextMessage = stateData?.mensagem
                            ? `${stateData.mensagem}$${value}`
                            : `$${value}`;
                          const sanitizedMessage =
                            sanitizeMensagem(nextMessage);

                          updatePipelineData((draft) => {
                            draft.updateOperationState(
                              selectedOperation,
                              (state) => {
                                state.mensagem = sanitizedMessage;
                                state.mappedFields =
                                  getMappedFieldsFromMessage(sanitizedMessage);
                              }
                            );
                            return;
                          });
                        }}
                        size="sm"
                        variant="outline"
                        isDisabled={isDisabled}
                      >
                        {name}
                      </Button>
                    );
                  })}
                </Grid>
              </Popover>
            </Flex>

            <InputField
              isDisabled={isDisabled}
              variant="filterInput"
              value={stateData?.mensagem}
              onChange={(event) => {
                const sanitizedMessage = sanitizeMensagem(event.target.value);

                updatePipelineData((draft) => {
                  draft.updateOperationState(selectedOperation, (state) => {
                    state.mensagem = sanitizedMessage;
                    state.mappedFields =
                      getMappedFieldsFromMessage(sanitizedMessage);
                  });
                  return;
                });
              }}
              inputProps={{
                as: "textarea",
                height: "118px",
                p: "10px 16px"
              }}
            />

            <Text
              mt="8px"
              textAlign="end"
              color={
                mensagemLength > 160 ? "var(--chakra-colors-secondary-400)" : ""
              }
            >
              {mensagemLength}/160
            </Text>
          </FilterCard>

          <FilterCard
            label="Pré-visualização"
            isDisabled={isDisabled}
            fullWidth
          >
            <Textarea
              value={getPreVisualizacao()}
              disabled
              height="130px"
              autoComplete="new-password"
              border="1px solid"
              borderColor="#E2E8F0"
              borderRadius="10px"
              fontSize="13px"
              bg="#F8FAFC"
              color="#90a1b9"
              _focus={{ outline: "none", boxShadow: "none" }}
              _hover={{}}
            />
          </FilterCard>
        </FilterGrid>
      </SidePanelAccordion>
      {selectedStateId != null &&
        pipelineData &&
        diagnosticUpdatePipelineData && (
          <DiagnosticPanel
            selectedStateId={selectedStateId}
            pipelineData={pipelineData}
            operation="SMS"
            updatePipelineData={diagnosticUpdatePipelineData}
          />
        )}
    </Stack>
  );
}
